define((require) => {
    "use strict";

    const {sortBy}             = require("underscore");
    const ImprovedModel        = require("shared/zbone/ImprovedModel");
    const {dasherize}          = require("shared/util/StringUtils");
    const Backbone             = require("backbone");
    const RestApi              = require("shared/services/RestApi");
    const Preconditions        = require("shared/util/Preconditions");
    const GroupAssetCollection = require("shared/ui/groups/GroupAssetCollection");
    const GroupAsset           = require("shared/ui/groups/GroupAsset");
    const _get                 = require("lodash/get");
    /**
     * The abstract base class for creating groups.
     *
     * @see  BreakpointGroup
     * @see  GroupModelEditorMixin
     *
     * @class GroupModel
     */
    return ImprovedModel.extend("Group", {

        /**
         * Array containing the value class and asset (reference) (in that order) class (as constructor functions)
         * e.g. <tt>[Breakpoint, ExperienceModel]</tt>.<p>
         *
         * This static property must be implemented by the subclass.
         *
         * @type {Function[]}
         */
        types: null,

        defaults: {
            /**
             * The group name.
             *
             * @type {String}
             */
            name: "Untitled Group",

            /**
             * The internal entries associated with this group.
             *
             * @type {GroupAssetCollection}
             */
            entries: [],

            /**
             * The type of group.
             *
             * @type {String}
             */
            type: null,

            /**
             * The group state, e.g. <tt>NotPushed</tt>.
             * @type {String}
             *
             * @see PublishableState
             */
            state: null,

            /**
             * The company ID.
             *
             * @type {String}
             */
            companyId: undefined
        },

        relations: [
            {
                type: Backbone.Many,
                key: "entries",
                collectionType: GroupAssetCollection,
                options: {
                // Expose factory function for creating concrete assets - see *types*...
                    assetFactory(attributes) {
                        const Model = this._assetClass;
                        const resourceId = attributes.reference.resourceId;
                        let modelAttributes;

                        if (this._resolvedAssets) {
                            modelAttributes = this._resolvedAssets[resourceId];
                        } else {
                        // HACK: The 'resolvedAssets' are (currently) not exposed to the viewer.
                        // We will still construct the asset, but it will only contain the id (and it's default attributes).
                        // We can get away with this since the viewer (for now at least) only needs the id, e.g. asset.get("id") will
                        // still work...
                            modelAttributes = {
                                id: resourceId
                            };
                        }

                        return new Model(modelAttributes);
                    }
                }
            }],

        constructor(attributes) {
            Preconditions.checkState(!!this.types && this.types.length === 2);

            this._valueClass = this.types[0];
            this._assetClass = this.types[1];
            this._resolvedAssets = attributes.resolvedAssets;

            this.__super();
            // Pick static properties - the type
            const type = this.constructor.type;

            Preconditions.checkArgument(type, "A group model must specify a type via a static property.");

            this.set("type", type, {silent: true});

            this._valueType = GroupAsset.typeFromValueClass(this._valueClass);
        },

        /**
         * The type (class/constructor function) of value. Used for type checking - e.g. when attaching assets.
         *
         * @type {Function}
         */
        _valueClass: null,

        /**
         *
         * @type {String}
         */
        _valueType: null,

        /**
         * The type (class/constructor function) of asset. Used for type checking - e.g. when attaching asserts.
         *
         * @type {Function}
         */
        _assetClass: null,

        url() {
            // Fix of the BreakPoint group error
            // Do no need to request urls that are not exists
            switch (true) {
                // Check if it's duplicate
                // Check if it's synced group and exists
                case this.has("copyOf"):
                case (this.syncState !== undefined && this.syncState !== null && _get(this, "syncState.actual.state") !== "unsynced"):
                case this.get("state") !== null:
                    return this.isNew() ? RestApi.pathToCollection(RestApi.Collections.GROUPS) : RestApi.pathToSpecificResource(RestApi.Collections.GROUPS, this.id);
                default:
                    return null;
            }
        },

        /**
         * @override
         * @inheritDoc
         */
        parse(response) {
            // When fetching, e.g. group.fetch() - we need to store a reference to the
            // latest 'resolvedAssets' maps, see the 'assetFactory' above...
            this._resolvedAssets = response.resolvedAssets;

            return this.__super();
        },

        // TODO: Consider returning an array of concrete types, e.g. *GroupEntry*...?
        /**
         * Returns a 2D array containing the <tt>[asset, value]</tt> pairs for each entry.
         *
         * @return {[asset, value][]} A 2D array containing the <tt>[asset, reference]</tt> pairs for each entry.
         */
        entries() {
            return this.get("entries").map((internalAsset) => [internalAsset.get("asset"), internalAsset.get("value")]);
        },

        /**
         * Returns all unique assets with values sorted by experiences/assets
         */
        getUniqueEntries() {
            const assets = this.get("entries")
                .chain()
                .reduce((assetsMap, internalAsset) => {
                    const asset = internalAsset.get("asset");

                    if (asset) {
                        const id = asset.get("id");
                        assetsMap[id] = [asset, internalAsset.get("value")];
                    }

                    return assetsMap;
                }, {})
                .values()
                .value();

            return sortBy(assets, (asset) => asset[0].get("createdAt"));
        },

        /**
         * Get unique assets referred by this group. This differs from method above, which can contains duplicated assets
         * Get unique (resolved) assets contained in this group. This differs from method above, which can contain
         * duplicated assets
         *
         * @return {Array} An array of (resolved) assets; never null, but can be empty.
         */
        getAssets() {
            return this.get("entries")
                .chain()
                .reduce((assetsMap, internalAsset) => {
                    const asset = internalAsset.get("asset");
                    if (asset) {
                        assetsMap[asset.get("id")] = asset;
                    }
                    return assetsMap;
                }, {})
                .values()
                .value();
        },

        /**
         * Get unique and sorted assets contained in this group.
         *
         * @param  {String} attribute The attribute to sort by; defaulting to <tt>createdAt</tt>.
         * @return {Array} An array of assets sorted by <tt>attribute</tt>; never falsey, but can be empty.
         */
        getSortedAssets(attribute) {
            const assets = this.getAssets();

            attribute = attribute || "createdAt";

            return sortBy(assets, (asset) => asset.get(attribute));
        },

        /**
         * Get the all unique values used in this group
         *
         * @returns {Backbone.Model} An array of value models
         */
        getValues() {
            return this.get("entries").reduce((values, entry) => {
                if (entry.get("value")) {
                    values.push(entry.get("value"));
                }

                return values;
            }, []);
        },

        /**
         * Returns the asset from the specified group value.
         *
         * @param  {*} value The group value.
         * @return {*}  The asset, e.g. an <tt>ExperienceModel</tt>.
         */
        assetFromValue(value) {
            // TODO: this will not work in the general case, needs a equal method or a comparator function - e.g. to compare on threshold, not on model instance
            const found = this.get("entries").find((entry) => entry.get("value") === value);
            return found && found.get("asset");
        },

        /**
         * Find the entry that is associated with an asset of provided id
         *
         * @param assetId
         * @returns {*}
         */
        findEntryByAssetId(assetId) {
            let found = null;
            let asset = null;

            found = this.get("entries").find((entry) => {
                asset = entry.get("asset");
                return asset && asset.get("id") === assetId;
            });

            return found;
        },

        findAssetById(assetId) {
            const entry = this.findEntryByAssetId(assetId);
            return entry && entry.get("asset");
        },

        getNumOfAssets() {
            return this.getAssets().length;
        },

        getFirstAsset() {
            return this.getSortedAssets()[0];
        },

        getDashedType() {
            return dasherize(this.get("type")).substring(1);
        },

        /**
         * Whether a group is considered as one unit when being published and scheduled.
         * For example, breakpoint group is one unit whereas localization group is not.
         *
         * Also, if it is considered one unit, the group can be viewed with an URL.
         *
         * @returns {Boolean}   true if it is consider one unit, false otherwise.
         */
        isOneUnit() {
            return true;
        },

        isInactive() {
            // Only inactive filters will actually push the state, and then as "Inactive", but for legacy reasons,
            // we have to handle cases where a state is marshaled anyway... :/
            return this.get("state") === "Inactive"; // Viewer thing, do not include complex enum state for this!
        }

    }, {

        /**
         * The type of group, e.g. <tt>BreakpointGroup</tt>.<p>
         *
         * This static property must be implemented by the subclass.
         *
         * @type {String}
         */
        type: null
    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/groups/GroupModel.js